/*
Copyright (c) 2026 Wind River Systems, Inc.

The right to copy, distribute, modify, or otherwise make use of this software may be licensed only pursuant to the terms of an applicable Wind River license agreement.
*/
import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTreeModule } from '@angular/material/tree';
import { MatRippleModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';

// Angular Services/Guards/Pipes
// import { HttpInterceptorService } from './services/utils/http-interceptor.service';
import { MomentModule } from 'ngx-moment';
import { NgxFilesizeModule } from 'ngx-filesize';

// Shared Libraries
import {
  DismissibleFlagModule,
  WRLicenseModule,
  WRNavbarModule,
  WRProfileModule,
  WRSidenavModule,
  WRSettingsModule,
  SettingsService,
  ThemingService,
  SnackBarService
} from '@windriver/ui';

import {
  WRAdminGuard,
  AuthService,
  TokenService,
  RolesService,
  HttpInterceptorService,
  WRAuthenticationModule,
  ConfigService
} from '@windriver/authentication';

// Components
import { HomeLayoutComponent } from './layouts/home-layout/home-layout.component';
import { LoginLayoutComponent } from './layouts/login-layout/login-layout.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { SettingsComponent } from './components/settings/settings.component';
import { AppListComponent } from './components/app-list/app-list.component';
import { AppItemComponent } from './components/app-list/app-item/app-item.component';
import { ProductsComponent } from './components/products/products.component';
import { ExternalLinkComponent } from './components/external-link/external-link.component';
import { HelpFaqComponent } from './components/help-faq/help-faq.component';
import { FeedbackComponent } from './components/feedback/feedback.component';
import { FileSharingComponent } from './components/file-sharing/file-sharing.component';
import { OrderDetailsComponent } from './components/products/order-details/order-details.component';
import { ReleaseNotesComponent } from './components/products/release-notes/release-notes.component';
import { SysNetRequirementsComponent } from './components/products/sys-net-requirements/sys-net-requirements.component';
import { SearchCustomersComponent } from './components/products/search-customers/search-customers.component';
import { RecentCustomersComponent } from './components/products/recent-customers/recent-customers.component';
import { CurrentUserService } from './services/current-user/current-user.service';
import { OfflineInstallHistoryComponent } from './components/products/offline-install-history/offline-install-history.component';
import { LicenseAgreementComponent } from './components/products/license-agreement/license-agreement.component';
import { SearchableSelectComponent } from './components/searchable-select/searchable-select.component';
import { TrackingImpersonationBannerComponent } from './components/products/banner/tracking-impersonation/tracking-impersonation-banner.component';
import { PatchNotesComponent } from './components/patch-notes/patch-notes.component';

@NgModule({
  declarations: [
    AppComponent,
    HomeLayoutComponent,
    LoginLayoutComponent,
    NavbarComponent,
    SidenavComponent,
    SettingsComponent,
    AppListComponent,
    AppItemComponent,
    ProductsComponent,
    ExternalLinkComponent,
    HelpFaqComponent,
    FeedbackComponent,
    FileSharingComponent,
    OrderDetailsComponent,
    ReleaseNotesComponent,
    SysNetRequirementsComponent,
    SearchCustomersComponent,
    RecentCustomersComponent,
    OfflineInstallHistoryComponent,
    LicenseAgreementComponent,
    SearchableSelectComponent,
    TrackingImpersonationBannerComponent,
    PatchNotesComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatToolbarModule,
    MatMenuModule,
    MatDividerModule,
    MatSidenavModule,
    MatTreeModule,
    MatRippleModule,
    MatDialogModule,
    MatTabsModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatExpansionModule,
    MatRadioModule,
    MatSnackBarModule,
    MatPaginatorModule,
    BrowserAnimationsModule,
    MatTooltipModule,
    WRAuthenticationModule,
    WRNavbarModule,
    WRSidenavModule,
    WRProfileModule,
    WRLicenseModule,
    DismissibleFlagModule,
    WRSettingsModule,
    MomentModule,
    NgxFilesizeModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
    AuthService,
    TokenService,
    CurrentUserService,
    SnackBarService,
    SettingsService,
    ThemingService,
    RolesService,
    WRAdminGuard,
    ConfigService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
